import React, { Component } from 'react';
import { Container } from '../../components';
import { api } from './api';
import { MOCK_DATA } from './data';
import {
  CommonContainer, Title, Grid, Section, SubTitle, Button, PersonItem, Badge,
  AddButton, RemoveButton, Input, BusPassengersList, SearchResultsList,
  SearchSection, UndoButton, LogItem, StickyTopBar, StickyPanel, Modal,
  ConfirmDialog
} from './partials';



export class Bus extends Component {
  searchInputRef = React.createRef();

  state = {
    selectedBusId: null,
    people: [],
    buses: [],
    assignments: {}, // { busId: [personId, personId, ...] }
    searchTerm: '',
    history: [], // [{ assignments: {}, description: string }]
    familyPanelData: null, // { relativeName: string, members: [people] }
    showHistoryModal: false, // Modal visibility
    confirmDialog: null, // { personId, busId, personName } or null
    searchFocused: false, // Track if search input is focused
    loading: true,
    error: null,
    mockupMode: false
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  fetchData = async () => {
    try {
      this.setState({ loading: true, error: null });

      // Parallel fetch of static data
      const [people, buses] = await Promise.all([
        api.getPeople('boda'),
        api.getBuses('boda')
      ]);

      if (this._isMounted) {
        this.setState({
          people,
          buses,
          loading: false,
          mockupMode: false
        });

        // Start polling for dynamic assignments
        this.startPolling();
      }

    } catch (err) {
      console.error('API Error, switching to Mockup Mode:', err);
      if (this._isMounted) {
        this.setState({
          people: MOCK_DATA.people,
          buses: MOCK_DATA.buses,
          assignments: MOCK_DATA.assignments,
          loading: false,
          mockupMode: true
        });
      }
    }
  };

  startPolling = () => {
    this.fetchAssignments();
    this.pollingInterval = setInterval(this.fetchAssignments, 3000);
  };

  fetchAssignments = async () => {
    if (!this._isMounted) return;
    try {
      const assignments = await api.getAssignments('boda');
      if (this._isMounted) {
        this.setState({ assignments });
      }
    } catch (err) {
      console.warn('Polling error:', err);
      // Optional: stop polling if too many errors? 
    }
  };

  addToHistory = (description, undoAction) => {
    this.setState(prevState => ({
      history: [
        ...prevState.history,
        {
          assignments: JSON.parse(JSON.stringify(prevState.assignments)),
          description,
          undoAction
        }
      ]
    }));
  };

  handleUndo = async () => {
    const { history, mockupMode } = this.state;
    if (history.length === 0) return;

    const lastItem = history[history.length - 1];
    const { undoAction } = lastItem;

    if (undoAction && !mockupMode) {
      try {
        if (undoAction.type === 'REMOVE') {
          // Deshacer una adición -> BORRAR
          await api.removeAssignment(undoAction.busId, undoAction.personId);
        } else if (undoAction.type === 'ADD') {
          // Deshacer un borrado -> AÑADIR (volver a asignar)
          await api.assignPerson(undoAction.busId, undoAction.personId);
        }
      } catch (err) {
        alert('Error al deshacer cambios en el servidor: ' + err.message);
        return; // No deshacemos en local si falla en servidor para no desincronizar
      }
    }

    this.setState(prevState => {
      const newHistory = [...prevState.history];
      const lastState = newHistory.pop();
      return {
        assignments: lastState.assignments,
        history: newHistory,
        familyPanelData: null // Close panel on undo
      };
    });
  };

  handleSelectBus = (busId) => {
    this.setState({ selectedBusId: busId, familyPanelData: null });
  };

  handleToggleHistoryModal = () => {
    this.setState(prevState => ({ showHistoryModal: !prevState.showHistoryModal }));
  };

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleSearchFocus = () => {
    this.setState({ searchFocused: true });

    // En mobile, hacer scroll y bloquear scroll del body
    if (window.innerWidth < 769) {
      // Bloquear scroll del body
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        // Scroll to top para la vista fullscreen
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  handleSearchBlur = () => {
    this.setState({ searchFocused: false });

    // Restaurar scroll del body en mobile
    if (window.innerWidth < 769) {
      document.body.style.overflow = '';
    }
  };

  handleAddPerson = async (personId) => {
    const { selectedBusId, assignments, people, buses, mockupMode } = this.state;
    if (!selectedBusId) return;

    const currentBusAssignments = assignments[selectedBusId] || [];
    const bus = buses.find(b => b.id === selectedBusId);
    const person = people.find(p => p.id === personId);

    // Check capacity
    if (currentBusAssignments.length >= bus.capacity) {
      alert('¡Este autobús está completo!');
      return;
    }

    // Validation: Check for Bus Type compatibility
    // Get all buses the person is currently assigned to
    const assignedBusIds = Object.keys(assignments).filter(bId => assignments[bId].includes(personId));
    const assignedBuses = buses.filter(b => assignedBusIds.includes(String(b.id)) || assignedBusIds.includes(Number(b.id)));

    // Check for conflicts based on type
    const targetType = bus.type; // 'ida', 'vuelta', 'ida/vuelta'

    // Conflict Rules:
    // - 'ida/vuelta' conflicts with ANY existing assignment (assumed strict)
    // - 'ida' conflicts with existing 'ida' or 'ida/vuelta'
    // - 'vuelta' conflicts with existing 'vuelta' or 'ida/vuelta'

    let hasConflict = false;
    let conflictBusName = '';

    if (assignedBuses.length > 0) {
      if (targetType === 'ida/vuelta') {
        hasConflict = true;
        conflictBusName = assignedBuses[0].name; // Any existing is a conflict
      } else {
        // Target is 'ida' or 'vuelta'
        const conflictBus = assignedBuses.find(b => b.type === targetType || b.type === 'ida/vuelta');
        if (conflictBus) {
          hasConflict = true;
          conflictBusName = conflictBus.name;
        }
      }
    }

    if (hasConflict) {
      alert(`¡Esta persona ya está asignada en un autobús incompatible (${conflictBusName})!`);
      return;
    }

    // ADD PERSON API CALL
    try {
      if (!mockupMode) {
        await api.assignPerson(selectedBusId, personId);
      }

      this.addToHistory(`Añadido ${person.name} ${person.lastNames} a ${bus.name}`, { type: 'REMOVE', busId: selectedBusId, personId });

      this.setState(prevState => {
        const newAssignments = {
          ...prevState.assignments,
          [selectedBusId]: [...(prevState.assignments[selectedBusId] || []), personId]
        };

        // Check Family Logic - UPDATED: Always update panel if person has family
        let nextPanelData = prevState.familyPanelData;

        if (person.family) {
          const familyMembers = this.state.people.filter(p =>
            p.family === person.family &&
            p.id !== person.id
          );

          if (familyMembers.length > 0) {
            nextPanelData = {
              relativeName: person.name + ' ' + person.lastNames,
              members: familyMembers,
              familyName: person.familyName
            };
          } else {
            // If person has family but no other members, ensure panel is closed if it was open for this family
            if (nextPanelData && nextPanelData.relativeName === person.name) {
              nextPanelData = null;
            }
          }
        }

        // AUTO-CLOSE CHECK: If all members in the panel are assigned (to any bus), close it
        if (nextPanelData) {
          const allMembersAssigned = nextPanelData.members.every(member => {
            return Object.values(newAssignments).some(list => list.includes(member.id));
          });

          if (allMembersAssigned) {
            nextPanelData = null;
          }
        }

        return {
          assignments: newAssignments,
          familyPanelData: nextPanelData
        };
      });
    } catch (err) {
      alert('Error asignando: ' + err.message);
    }
  };

  handleRemovePerson = (personId, busId) => {
    const { people } = this.state;
    const person = people.find(p => p.id === personId);
    this.setState({
      confirmDialog: {
        personId,
        busId,
        personName: `${person.name} ${person.lastNames}`
      }
    });
  };

  confirmRemovePerson = async () => {
    const { confirmDialog, people, buses, mockupMode } = this.state;
    if (!confirmDialog) return;

    const { personId, busId } = confirmDialog;
    const person = people.find(p => p.id === personId);
    const bus = buses.find(b => b.id === busId);

    try {
      if (!mockupMode) {
        await api.removeAssignment(busId, personId);
      }

      this.addToHistory(`Eliminado ${person.name} ${person.lastNames} de ${bus.name}`, { type: 'ADD', busId, personId });

      this.setState(prevState => ({
        assignments: {
          ...prevState.assignments,
          [busId]: prevState.assignments[busId].filter(id => id !== personId)
        },
        confirmDialog: null
      }));
    } catch (err) {
      alert('Error eliminando asignación: ' + err.message);
    }
  };

  cancelRemovePerson = () => {
    this.setState({ confirmDialog: null });
  };

  handleClosePanel = () => {
    this.setState({ familyPanelData: null });
  };

  isPersonAssigned = (personId) => {
    const { assignments, buses } = this.state;
    const busId = Object.keys(assignments).find(bId => assignments[bId].includes(personId));
    return busId ? buses.find(b => b.id === Number(busId)) : null;
  };

  render() {
    const { selectedBusId, assignments, searchTerm, history, familyPanelData, showHistoryModal, confirmDialog, people, buses, loading, error, mockupMode } = this.state;

    if (loading) return <Container>Loading...</Container>;
    if (error) return <Container>{error}</Container>;

    const selectedBus = buses.find(b => b.id === selectedBusId);

    // Calculate stats
    const currentAssignments = selectedBus ? (assignments[selectedBus.id] || []) : [];
    const occupancy = currentAssignments.length;
    const capacity = selectedBus ? selectedBus.capacity : 0;
    const remaining = capacity - occupancy;

    // Filter and sort people alphabetically
    // Filter and sort people
    const filteredPeople = people
      .filter(p => {
        const fullString = `${p.name} ${p.lastNames}`.toLowerCase();
        const matchesSearch = fullString.includes(searchTerm.toLowerCase());

        // If searching, show matches. If not searching, only show unassigned people.
        if (searchTerm) {
          return matchesSearch;
        } else {
          // If not searching, hide if assigned to ANY bus
          const isAssigned = this.isPersonAssigned(p.id);
          return !isAssigned;
        }
      })
      .sort((a, b) => {
        // If searching, sort unassigned first, then assigned
        if (searchTerm) {
          const aAssigned = this.isPersonAssigned(a.id);
          const bAssigned = this.isPersonAssigned(b.id);

          if (aAssigned && !bAssigned) return 1;
          if (!aAssigned && bAssigned) return -1;
        }
        // Alphabetical sort (secondary or primary if no search)
        return a.name.localeCompare(b.name);
      });

    return (
      <Container>
        {/* Mockup Mode Banner */}
        {mockupMode && (
          <div style={{
            background: '#f39c12',
            color: '#fff',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontWeight: 'bold',
            border: '2px solid #e67e22'
          }}>
            ⚠️ MODO OFFLINE / MOCKUP - Sin conexión al servidor
          </div>
        )}

        {/* Fixed Top Bar - Only show when bus is selected and not searching on mobile */}
        {selectedBus && (!this.state.searchFocused || window.innerWidth >= 769) && (
          <StickyTopBar>
            <div className="bar-content">
              <div className="bus-info" onClick={() => this.handleSelectBus(null)}>
                <div>
                  <div className="bus-name">🚌 {selectedBus.name}</div>
                  <div className="bus-stats">
                    {occupancy} / {capacity}
                  </div>
                </div>
                {/* <span style={{ fontSize: '1.2rem' }}>↩</span> */}
              </div>
              <button className="history-button" onClick={this.handleToggleHistoryModal}>
                <div>
                  📋 Historial<br />
                  de cambios</div>
                <span>({history.length})</span>
              </button>
            </div>
          </StickyTopBar>
        )}

        <CommonContainer hasPanel={!!familyPanelData} hasTopBar={!!selectedBus}>
          {(!this.state.searchFocused || window.innerWidth >= 769) && (
            <Title>Gestión de Autobuses</Title>
          )}

          {/* Bus Selection - Only show when NO bus is selected */}
          {!selectedBus && (
            <Section style={{ maxWidth: '600px', margin: '0 auto' }}>
              <SubTitle>Autobuses Disponibles</SubTitle>
              {buses.map(bus => {
                const busOccupancy = (assignments[bus.id] || []).length;
                return (
                  <Button
                    key={bus.id}
                    active={false}
                    onClick={() => this.handleSelectBus(bus.id)}
                  >
                    <div style={{ fontWeight: 'bold' }}>{bus.name}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                      {busOccupancy} / {bus.capacity} pasajeros
                    </div>
                  </Button>
                );
              })}
            </Section>
          )}

          {/* Main Content - Only show when bus is selected */}
          {selectedBus && (
            <div style={{ maxWidth: '900px' }}>
              {/* Add People Section - NOW FIRST */}
              <SearchSection searchFocused={this.state.searchFocused}>
                {(!this.state.searchFocused || window.innerWidth >= 769) && (
                  <SubTitle>Añadir Pasajeros</SubTitle>
                )}
                <Input
                  ref={this.searchInputRef}
                  type="text"
                  placeholder="Buscar persona..."
                  value={searchTerm}
                  onChange={this.handleSearch}
                  onFocus={this.handleSearchFocus}
                  onBlur={this.handleSearchBlur}
                />

                <SearchResultsList searchFocused={this.state.searchFocused}>
                  {filteredPeople.length === 0 ? (
                    <div style={{ opacity: 0.7, fontStyle: 'italic' }}>No se encontraron personas</div>
                  ) : (
                    filteredPeople.map(person => {
                      const assignedBus = this.isPersonAssigned(person.id);
                      const isAssignedToCurrent = assignedBus && assignedBus.id === selectedBus.id;
                      const isFull = remaining <= 0;

                      if (isAssignedToCurrent) return null;

                      // CHECK FOR CONFLICTS (Same logic as handleAddPerson)
                      // Get all buses this person is assigned to
                      const myAssignedBusIds = Object.keys(this.state.assignments).filter(bId => this.state.assignments[bId].includes(person.id));
                      const myAssignedBuses = this.state.buses.filter(b => myAssignedBusIds.includes(String(b.id)) || myAssignedBusIds.includes(Number(b.id)));

                      let hasConflict = false;
                      if (myAssignedBuses.length > 0) {
                        if (selectedBus.type === 'ida/vuelta') {
                          hasConflict = true;
                        } else {
                          // Target is 'ida' or 'vuelta'
                          // Conflict if they are already in a bus of THIS type or 'ida/vuelta'
                          const conflictBus = myAssignedBuses.find(b => b.type === selectedBus.type || b.type === 'ida/vuelta');
                          if (conflictBus) hasConflict = true;
                        }
                      }

                      return (
                        <PersonItem key={person.id}>
                          <div>
                            <Badge type={person.need}>{person.need}</Badge>
                            <div>
                              {person.name} {person.lastNames}
                              {assignedBus && (
                                <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem', opacity: 0.7 }}>
                                  (En {assignedBus.name})
                                </span>
                              )}
                              {person.family && (
                                <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem', color: '#f1c40f' }}>
                                  ★
                                </span>
                              )}
                            </div>
                          </div>

                          {!hasConflict && (
                            <AddButton
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                this.handleAddPerson(person.id);
                                this.state.searchFocused && this.handleSearchBlur();
                              }}
                              disabled={isFull}
                            >
                              {isFull ? 'Lleno' : 'Añadir'}
                            </AddButton>
                          )}
                        </PersonItem>
                      );
                    })
                  )}
                </SearchResultsList>
              </SearchSection>

              {/* Selected Bus Info - NOW SECOND - Hidden on mobile when search is focused */}
              {(!this.state.searchFocused || window.innerWidth >= 769) && (
                <Section>
                  <SubTitle>
                    {selectedBus.name}
                    <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>
                      Asientos libres: <strong>{remaining}</strong>
                    </span>
                  </SubTitle>

                  <BusPassengersList>
                    {currentAssignments.length === 0 ? (
                      <div style={{ padding: '1rem', fontStyle: 'italic', opacity: 0.7 }}>Autobús vacío</div>
                    ) : (
                      currentAssignments
                        .map(personId => people.find(p => p.id === personId))
                        .filter(p => !!p)
                        .map(person => (
                          <PersonItem key={person.id}>
                            <div>
                              <Badge type={person.need}>{person.need}</Badge>
                              <strong>{person.name} {person.lastNames}</strong>
                            </div>
                            <RemoveButton onClick={() => this.handleRemovePerson(person.id, selectedBus.id)}>
                              Sacar
                            </RemoveButton>
                          </PersonItem>
                        ))
                    )}
                  </BusPassengersList>
                </Section>
              )}
            </div>
          )}

          {/* History Modal */}
          {showHistoryModal && (
            <Modal onClick={this.handleToggleHistoryModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Historial de Acciones</h2>
                  <button className="modal-close" onClick={this.handleToggleHistoryModal}>×</button>
                </div>
                <UndoButton
                  onClick={() => {
                    this.handleUndo();
                    if (history.length === 1) this.handleToggleHistoryModal(); // Close if last item
                  }}
                  disabled={history.length === 0}
                >
                  Deshacer Último Cambio
                </UndoButton>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '1rem' }}>
                  {history.length === 0 && <div style={{ opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>Sin acciones recientes</div>}
                  {[...history].reverse().map((item, index) => (
                    <LogItem key={index}>{item.description}</LogItem>
                  ))}
                </div>
              </div>
            </Modal>
          )}

          {/* Sticky Panel */}
          {familyPanelData && selectedBus && (
            <StickyPanel>
              <div className="panel-header">
                <h3>
                  {familyPanelData.familyName ? familyPanelData.familyName : `Familia de ${familyPanelData.relativeName}`}
                </h3>
                <button className="close-button" onClick={this.handleClosePanel}>
                  <span style={{ fontSize: '1rem', marginRight: '0.5rem' }}>Cerrar</span>
                  ×
                </button>
              </div>

              <div className="members-grid">
                {familyPanelData.members.map(member => {
                  const assignedBus = this.isPersonAssigned(member.id);
                  const isAssignedToCurrent = assignedBus && assignedBus.id === selectedBus.id;

                  let buttonText = 'Añadir';
                  let buttonColor = 'var(--success, #2ecc71)';
                  let isDisabled = false;

                  if (isAssignedToCurrent) {
                    buttonText = 'Añadido';
                    buttonColor = 'var(--primary)';
                    isDisabled = true;
                  } else if (assignedBus) {
                    buttonText = `En ${assignedBus.name}`;
                    buttonColor = '#e67e22'; // Warning color
                    isDisabled = true;
                  } else if (remaining <= 0) {
                    buttonText = 'Lleno';
                    isDisabled = true;
                  }

                  return (
                    <div key={member.id} className="member-card">
                      <div className="member-info">
                        <Badge type={member.need}>{member.need}</Badge>
                        <span>{member.name} {member.lastNames}</span>
                      </div>
                      <AddButton
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => this.handleAddPerson(member.id)}
                        disabled={isDisabled}
                        style={{ background: isDisabled ? '#7f8c8d' : buttonColor, minWidth: '80px' }}
                      >
                        {buttonText}
                      </AddButton>
                    </div>
                  );
                })}
              </div>
            </StickyPanel>
          )}

          {/* Confirm Dialog */}
          {confirmDialog && (
            <ConfirmDialog onClick={this.cancelRemovePerson}>
              <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-message">
                  ¿Estás seguro de que quieres sacar a <strong>{confirmDialog.personName}</strong> del autobús?
                </div>
                <div className="dialog-buttons">
                  <button className="dialog-button cancel" onClick={this.cancelRemovePerson}>
                    Cancelar
                  </button>
                  <button className="dialog-button confirm" onClick={this.confirmRemovePerson}>
                    Sacar
                  </button>
                </div>
              </div>
            </ConfirmDialog>
          )}

        </CommonContainer>
      </Container>
    );
  }
}